
import { Textarea } from "@/components/ui/textarea";
import { useEffect, useState } from "react";

type CourseBenefitsFieldProps = {
  benefits: any;
  onChange: (value: any) => void;
  isEditing: boolean;
};

export function CourseBenefitsField({ 
  benefits, 
  onChange, 
  isEditing 
}: CourseBenefitsFieldProps) {
  // Store the formatted benefits as a string for editing
  const [benefitsString, setBenefitsString] = useState<string>("");

  // Format JSON data for display or editing
  const formatJsonForDisplay = (jsonData: any) => {
    if (!jsonData) return '';
    return typeof jsonData === 'string' ? jsonData : JSON.stringify(jsonData, null, 2);
  };
  
  // Set up benefits string when component mounts or benefits change
  useEffect(() => {
    console.log('Benefits received:', benefits);
    setBenefitsString(formatJsonForDisplay(benefits));
  }, [benefits]);
  
  // Handle benefits text change
  const handleBenefitsChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setBenefitsString(value);
    onChange(value);
  };
  
  // Render a benefit item based on its type
  const renderBenefitItem = (benefit: any, index: number) => {
    // If the benefit is an object with title/description
    if (benefit && typeof benefit === 'object' && 'title' in benefit) {
      return (
        <li key={index}>
          <div>
            <span className="font-medium">{benefit.title}</span>
            {benefit.description && (
              <p className="text-sm text-gray-600 dark:text-gray-400">{benefit.description}</p>
            )}
          </div>
        </li>
      );
    }
    // If the benefit is a simple string
    else if (typeof benefit === 'string') {
      return <li key={index}>{benefit}</li>;
    }
    // Fallback for any other case
    else {
      return <li key={index}>{JSON.stringify(benefit)}</li>;
    }
  };

  // Parse and render benefits
  const renderBenefits = () => {
    if (!benefits) return <div>No benefits listed</div>;
    
    try {
      // Handle already parsed object
      if (typeof benefits === 'object' && Array.isArray(benefits)) {
        return (
          <ul className="list-disc pl-5 space-y-1">
            {benefits.map((benefit, index) => renderBenefitItem(benefit, index))}
          </ul>
        );
      }
      
      // Handle string that needs parsing
      if (typeof benefits === 'string' && benefits.trim()) {
        const parsedBenefits = JSON.parse(benefits);
        if (Array.isArray(parsedBenefits)) {
          return (
            <ul className="list-disc pl-5 space-y-1">
              {parsedBenefits.map((benefit, index) => renderBenefitItem(benefit, index))}
            </ul>
          );
        }
      }
      
      // Fallback for other formats
      return <div>{JSON.stringify(benefits, null, 2)}</div>;
    } catch (error) {
      console.error("Error rendering benefits:", error);
      return <div>Error displaying benefits: {String(error)}</div>;
    }
  };

  return (
    <div className="space-y-2">
      <label className="text-sm font-medium">Benefits</label>
      {isEditing ? (
        <div>
          <Textarea
            value={benefitsString}
            onChange={handleBenefitsChange}
            rows={4}
            placeholder='["Benefit 1", "Benefit 2", "Benefit 3"] or [{"title": "Benefit 1", "description": "Details here"}, ...]'
            className="font-mono"
          />
          <p className="text-xs text-muted-foreground mt-1">
            Enter benefits as a JSON array of strings or objects with title/description.
          </p>
        </div>
      ) : (
        <div className="whitespace-pre-wrap">
          {renderBenefits()}
        </div>
      )}
    </div>
  );
}
